/* ============================================================
   mm-prefetch — make the NEXT navigation feel instant.

   Three triggers, cheapest first:
     1. Viewport  — a link the customer can actually see is a link they
                    might tap, so it gets warmed once it has been on
                    screen briefly. Links below the fold cost nothing.
     2. Nav drawer— opening the menu is a strong "I'm about to navigate"
                    signal, so every destination in it is warmed at once,
                    ahead of any hover.
     3. Intent    — pointerenter / touchstart on any internal link, for
                    the links the two passes above never reached.

   Budget is sized from the device, not guessed: a phone on a metered
   connection warms far fewer pages than a desktop. Save-Data,
   prefers-reduced-data and 2g connections disable it entirely.

   Prefetch only downloads the document; it does not run its scripts,
   so no pixel double-fires and no analytics pollution.

   Pairs with the Speculation Rules block in snippets/cta-prefetch.liquid,
   which covers a.mm-btn hovers in Chromium. Both funnel through the same
   `seen` set via window.__mmPrefetched, so a URL is never warmed twice.
   ============================================================ */
(function () {
  'use strict';

  if (window.__mmPrefetchInit) return;        // double-include guard
  window.__mmPrefetchInit = true;

  var seen = window.__mmPrefetched = window.__mmPrefetched || {};
  var used = 0;

  /* ---------- opt out of anything that costs the customer money ---------- */
  function cheapDataOnly() {
    try {
      var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (c) {
        if (c.saveData) return true;
        if (/(^|-)2g$/.test(c.effectiveType || '')) return true;
      }
      if (window.matchMedia && window.matchMedia('(prefers-reduced-data: reduce)').matches) return true;
    } catch (e) { /* unknown connection — assume it's fine */ }
    return false;
  }
  if (cheapDataOnly()) return;

  /* ---------- budget from the actual device, per the brief ----------
     A 390px phone and a 1440px desktop should not warm the same number
     of documents. deviceMemory/hardwareConcurrency catch cheap Androids
     that report a wide viewport but choke on the extra work.

     Kept deliberately small. A ManMade PDP is ~180KB, so a generous budget
     turns into megabytes of speculative traffic for pages the customer never
     opens — that is bloat, not speed. These numbers cover the drawer burst
     (3-6 links) plus a couple of on-screen CTAs and stop there. */
  var BUDGET = (function () {
    var w = window.innerWidth || 1024;
    var mem = navigator.deviceMemory || 4;
    var cores = navigator.hardwareConcurrency || 4;
    var n;
    if (w < 480) n = 3;
    else if (w < 900) n = 4;
    else if (w < 1400) n = 6;
    else n = 8;
    if (mem && mem <= 2) n = Math.min(n, 2);
    else if (mem && mem <= 4) n = Math.min(n, 5);
    if (cores && cores <= 2) n = Math.min(n, 2);
    return n;
  })();

  /* ---------- which links are worth warming ---------- */
  var SKIP_PATH = /^\/(cart|checkout|account|orders|a\/|apps\/|tools\/)/;

  function candidate(a) {
    if (!a || !a.href) return null;
    if (a.target && a.target !== '_self') return null;
    if (a.hasAttribute('download') || a.hasAttribute('data-no-prefetch')) return null;
    var u;
    try { u = new URL(a.href, location.href); } catch (e) { return null; }
    if (u.origin !== location.origin) return null;           // same-origin only
    if (!/^https?:$/.test(u.protocol)) return null;          // no mailto:/tel:
    if (SKIP_PATH.test(u.pathname)) return null;             // never warm cart/checkout/account
    u.hash = '';
    var key = u.pathname + u.search;
    /* Current page by PATH, not path+query: ad landings carry ?utm_…&fbclid=…,
       so the PDP's own links (canonical product URL, ?variant= swatches) never
       matched the full URL and the page re-downloaded itself (~400KB on a phone).
       Pagination (?page=N) is a different document, so it stays eligible. */
    if (u.pathname === location.pathname && !/[?&]page=/.test(u.search)) return null; // current page
    if (seen[key]) return null;
    return key;
  }

  /* ---------- priority: the budget must buy the SALE PATH first ----------
     The budget is small on purpose, so the order it is spent in matters more
     than the size. A CTA above the fold outranks a blog link below it, and a
     product page (a step toward checkout) outranks an about page. */
  var CTA_SEL = 'a.mm-btn, [data-mm-cta], .mm-buybox a, .mm-sticky-atc a, .mm-drawer__bestseller-cta';

  function score(a, key) {
    var s = 0;
    try { if (a.matches && a.matches(CTA_SEL)) s += 100; } catch (e) { /* bad selector support */ }
    if (/^\/products\//.test(key)) s += 50;            // a PDP is a step to the sale
    else if (/^\/collections\//.test(key)) s += 30;
    else if (/^\/(pages|blogs)\//.test(key)) s += 5;
    try {
      var r = a.getBoundingClientRect();
      var vh = window.innerHeight || 800;
      if (r.bottom > 0 && r.top < vh) s += 25;         // above the fold, right now
    } catch (e) { /* detached node */ }
    return s;
  }

  /* Warm a set of links best-first, so a tight budget is never spent on the
     footer's "Accessibility" link while a buy CTA goes cold. */
  function warmRanked(nodes, extra) {
    var ranked = [];
    for (var i = 0; i < nodes.length; i++) {
      var key = candidate(nodes[i]);
      if (key) ranked.push({ key: key, s: score(nodes[i], key) });
    }
    ranked.sort(function (x, y) { return y.s - x.s; });
    for (var j = 0; j < ranked.length; j++) warm(ranked[j].key, extra);
  }

  /* ---------- the actual warm ---------- */
  var supportsRules = false;
  try {
    supportsRules = !!(HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules'));
  } catch (e) { supportsRules = false; }

  /* Hover / touch on a link is the strongest signal there is — it gets a
     small allowance of its own on top of BUDGET, so the viewport pass can
     never have spent everything before the customer actually points. */
  var INTENT_EXTRA = 4;

  function warm(key, extra) {
    if (!key || seen[key] || used >= BUDGET + (extra || 0)) return;
    seen[key] = 1;
    used++;
    if (supportsRules) {
      /* Speculation Rules gives a real prerender-grade prefetch in Chromium
         and reuses the browser's own dedupe across rule sets. */
      try {
        var s = document.createElement('script');
        s.type = 'speculationrules';
        s.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls: [key] }] });
        document.head.appendChild(s);
        return;
      } catch (e) { /* fall through to <link rel=prefetch> */ }
    }
    try {
      var l = document.createElement('link');
      l.rel = 'prefetch';
      l.as = 'document';
      l.href = key;
      document.head.appendChild(l);
    } catch (e) { /* nothing more we can do */ }
  }

  /* Warming must never compete with the page the customer is already
     looking at — everything is deferred to idle time. */
  var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1); };
  function warmSoon(key) { if (key) idle(function () { warm(key); }, { timeout: 2000 }); }

  /* ---------- 1. viewport: warm what the customer can see ---------- */
  function initViewport() {
    if (!('IntersectionObserver' in window)) return;
    /* Bulk warming of on-screen links is the speculative part — on a slow
       connection it only competes with the page being read. Drawer and
       intent warms (a navigation that is actually about to happen) stay. */
    if (window.mmSlowNet && window.mmSlowNet()) return;
    var timers = new WeakMap(), first = true;
    /* Every link is watched, but only parsed (candidate) once it is on screen.
       Top/bottom margin only, and real overlap required: a closed drawer sits
       flush against the screen's side edge and touching counts as
       intersecting, so the menu's and cart's links used to read as on screen
       and could take the budget before any of the page's own links. */
    var io = new IntersectionObserver(function (entries) {
      var on = [];
      entries.forEach(function (entry) {
        if (entry.isIntersecting && entry.intersectionRatio > 0) on.push(entry.target);
        else clearTimeout(timers.get(entry.target));
      });
      if (first) {
        /* Priority pass FIRST: spend the budget on what is on screen right
           now, best-first, before the observer starts warming whatever
           happens to scroll past. Without this the budget is awarded in DOM
           order, which on a PDP means the header nav wins and the buy CTA
           loses. */
        first = false;
        warmRanked(on);
        on.forEach(function (a) { io.unobserve(a); });
      } else {
        on.forEach(function (a) {
          /* A link that scrolls straight past is not intent. Only links
             that settle on screen for a beat are worth the bytes. */
          timers.set(a, setTimeout(function () {
            warmSoon(candidate(a));
            io.unobserve(a);
          }, 300));
        });
      }
      if (used >= BUDGET) io.disconnect();
    }, { rootMargin: '200px 0px', threshold: [0, 0.01] });

    var all = document.querySelectorAll('a[href]');
    for (var i = 0; i < all.length; i++) io.observe(all[i]);
    return io;
  }

  /* ---------- 2. nav drawer: opening the menu means "I'm navigating" ---------- */
  function initDrawer() {
    var drawer = document.getElementById('mm-drawer');
    if (!drawer || !('MutationObserver' in window)) return;

    var done = false;
    function warmDrawer() {
      if (done) return;
      done = true;                                   // one burst per page view
      /* Drawer links call warm() DIRECTLY — not warmSoon(). Opening the menu
         is the strongest navigation signal on the site, and the menu is only
         open a second or two before a tap, but requestIdleCallback is
         deferred while the page is still busy, which measured late enough to
         miss the tap entirely. */
      warmRanked(drawer.querySelectorAll('a[href]'));
    }

    if (drawer.hasAttribute('data-open')) warmDrawer();
    new MutationObserver(function () {
      if (drawer.hasAttribute('data-open')) warmDrawer();
    }).observe(drawer, { attributes: true, attributeFilter: ['data-open'] });

    /* Warm on the INTENT to open, not the open itself — the hamburger's
       pointerdown lands ~100ms before the drawer finishes animating. */
    var btn = document.querySelector('[data-mm-action="open-drawer"]');
    if (btn) {
      ['pointerenter', 'touchstart'].forEach(function (ev) {
        btn.addEventListener(ev, warmDrawer, { passive: true, once: true });
      });
    }
  }

  /* ---------- 3. hover / touch intent on any remaining link ---------- */
  function initIntent() {
    var opts = { capture: true, passive: true };
    function onIntent(e) {
      /* Bail before doing ANY work once the budget is gone, and unbind
         entirely — this runs on every pointer movement across the page, so
         it must not outlive its usefulness. */
      if (used >= BUDGET + INTENT_EXTRA) {
        document.removeEventListener('pointerenter', onIntent, opts);
        document.removeEventListener('touchstart', onIntent, opts);
        document.removeEventListener('pointerdown', onIntent, opts);
        return;
      }
      var a = e.target && e.target.closest && e.target.closest('a[href]');
      /* Straight to warm(), not warmSoon(): idle time on a busy page can
         arrive after the tap it was meant to beat. */
      if (a) warm(candidate(a), INTENT_EXTRA);
    }
    document.addEventListener('pointerenter', onIntent, opts);
    document.addEventListener('touchstart', onIntent, opts);
    document.addEventListener('pointerdown', onIntent, opts);
  }

  function start() {
    try { initDrawer(); } catch (e) { /* non-fatal */ }
    try { initIntent(); } catch (e) { /* non-fatal */ }
    /* Viewport scan waits for load so it can never contend with LCP. */
    var vpIo = null;
    function runViewport() { vpIo = initViewport(); }
    if (document.readyState === 'complete') idle(runViewport, { timeout: 3000 });
    else window.addEventListener('load', function () { idle(runViewport, { timeout: 3000 }); }, { once: true });
    /* Sections deferred by theme.liquid land one at a time after the scan: watch their links too. */
    document.addEventListener('mm:deferred-hydrated', function (e) {
      if (!vpIo || used >= BUDGET) return;
      var root = (e.detail && e.detail.root) || document;
      if (!root.querySelectorAll) return;
      var links = root.querySelectorAll('a[href]');
      for (var i = 0; i < links.length; i++) vpIo.observe(links[i]);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
